import React, { useState, useEffect } from 'react';
import { useParams, Link as ReactRouterLink } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  SimpleGrid,
  Progress,
  useColorModeValue,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  useDisclosure,
  Collapse,
} from '@chakra-ui/react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import Breadcrumbs from './shared/Breadcrumbs';
import LoadingSpinner from './shared/LoadingSpinner';
import GenerateTrancheModal from './GenerateTrancheModal';
import SendQueue from './SendQueue';
import EmailSendQueue from './EmailSendQueue';
import fetchWithAuth from '../utils/fetchWithAuth';

function LivePollingDashboard() {
  const { pollId } = useParams();
  const [poll, setPoll] = useState(null);
  const [audience, setAudience] = useState(null);
  const [progress, setProgress] = useState(null);
  const [demographics, setDemographics] = useState(null);
  const [tranches, setTranches] = useState([]);
  const [trancheCost, setTrancheCost] = useState(null);
  const [respondents, setRespondents] = useState([]);
  const [abResults, setAbResults] = useState(null);
  const [topline, setTopline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showOperations, setShowOperations] = useState(false);
  const [showSendQueue, setShowSendQueue] = useState(false);
  const [showEmailQueue, setShowEmailQueue] = useState(false);
  const [showRespondents, setShowRespondents] = useState(false);

  const { isOpen, onOpen, onClose } = useDisclosure();

  const bgColor = useColorModeValue('background.shade1');
  const boxBgColor = useColorModeValue('background.shade2');
  const borderColor = useColorModeValue('gray.300');

  useEffect(() => {
    fetchAllData();
    const interval = setInterval(fetchAllData, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollId]);

  const fetchAllData = async () => {
    try {
      setLoading(true);

      const [pollResponse, progressResponse, demographicsResponse, tranchesResponse, toplineResponse] =
        await Promise.all([
          fetchWithAuth(`/api/polls/${pollId}/`),
          fetchWithAuth(`/api/polls/${pollId}/progress/`),
          fetchWithAuth(`/api/polls/${pollId}/demographics/`),
          fetchWithAuth(`/api/polls/${pollId}/tranches/`),
          fetchWithAuth(`/api/polls/${pollId}/topline/`),
        ]);

      if (!pollResponse.ok) throw new Error('Failed to fetch poll');

      const pollData = await pollResponse.json();
      setPoll(pollData);

      if (pollData.audiences && pollData.audiences.length > 0) {
        const audienceResponse = await fetchWithAuth(`/api/audiences/${pollData.audiences[0]}/`);
        if (audienceResponse.ok) {
          setAudience(await audienceResponse.json());
        }
      }

      if (progressResponse.ok) setProgress(await progressResponse.json());
      if (demographicsResponse.ok) setDemographics(await demographicsResponse.json());

      if (tranchesResponse.ok) {
        const tranchesData = await tranchesResponse.json();
        setTranches(tranchesData.tranches || []);
        setTrancheCost(tranchesData.total_cost || null);
      }

      if (toplineResponse.ok) setTopline(await toplineResponse.json());

      // A/B results if variants exist
      if (pollData.invitation_variants && pollData.invitation_variants.length > 0) {
        try {
          const abResponse = await fetchWithAuth(`/api/polls/${pollId}/ab-results/`);
          if (abResponse.ok) setAbResults(await abResponse.json());
        } catch (e) {
          console.error('Failed to fetch A/B results:', e);
        }
      }

      setError(null);

      if (showRespondents) await fetchRespondents();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchRespondents = async () => {
    try {
      const response = await fetchWithAuth(`/api/polls/${pollId}/respondents/`);
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data)) setRespondents(data);
        else if (data.results) setRespondents(data.results);
        else setRespondents([]);
      }
    } catch (err) {
      console.error('Error fetching respondents:', err);
    }
  };

  const prepareComparisonData = () => {
    if (!demographics || !poll?.target_demographics || demographics.total_respondents === 0) return [];
    const data = [];
    Object.keys(poll.target_demographics).forEach((fieldName) => {
      const fieldConfig = poll.target_demographics[fieldName];
      const targets = fieldConfig?.targets || fieldConfig;
      const enabled = fieldConfig?.enabled !== undefined ? fieldConfig.enabled : true;
      if (!enabled || !targets || typeof targets !== 'object') return;
      const currentDist = demographics.demographics[fieldName] || [];
      Object.keys(targets).forEach((value) => {
        const currentItem = currentDist.find((item) => item.value === value);
        data.push({
          group: `${fieldName}: ${value}`,
          target: targets[value],
          current: currentItem ? currentItem.percentage : 0,
        });
      });
    });
    return data;
  };

  const handleTrancheGenerated = () => {
    fetchAllData();
    if (showRespondents) fetchRespondents();
  };

  if (loading && !poll) return <LoadingSpinner />;

  if (error) {
    return (
      <Box bg={bgColor} minHeight="100vh" py={8}>
        <Box maxWidth="800px" margin="0 auto" textAlign="center">
          <Text color="red.500">Error: {error}</Text>
          <Button as={ReactRouterLink} to="/pollsPage" mt={4}>Back to Polls</Button>
        </Box>
      </Box>
    );
  }

  if (!poll) {
    return (
      <Box bg={bgColor} minHeight="100vh" py={8}>
        <Text textAlign="center">Poll not found</Text>
      </Box>
    );
  }

  const comparisonData = prepareComparisonData();

  return (
    <Box bg={bgColor} minHeight="100vh" py={8}>
      <Box maxWidth="1200px" margin="0 auto" px={4}>
        <Breadcrumbs
          items={[
            { label: 'Home', path: '/' },
            { label: 'Polls', path: '/pollsPage' },
            { label: poll.title, path: `/manage-questions/${pollId}` },
            { label: 'Live Dashboard' },
          ]}
        />

        {/* Header */}
        <HStack justify="space-between" mb={6}>
          <VStack align="start" spacing={0}>
            <Heading as="h1" size="xl">{poll.title}</Heading>
            <HStack spacing={3} mt={1}>
              <Badge colorScheme={poll.state === 'live' ? 'green' : 'gray'} fontSize="sm">
                {poll.state.toUpperCase()}
              </Badge>
              {loading && <Text fontSize="xs" color="gray.400">Updating...</Text>}
            </HStack>
          </VStack>
          <HStack spacing={2}>
            <Button
              as={ReactRouterLink}
              to={`/live-poll/${pollId}/monitor`}
              colorScheme="teal"
              variant="outline"
              size="sm"
            >
              Survey Monitor
            </Button>
            <Button
              as={ReactRouterLink}
              to={`/analytics/${pollId}`}
              colorScheme="purple"
              size="sm"
            >
              Analytics
            </Button>
            <Button onClick={fetchAllData} size="sm" isLoading={loading}>
              Refresh
            </Button>
            {poll.state === 'live' && (
              <Button onClick={onOpen} colorScheme="blue" size="sm">
                Generate Tranche
              </Button>
            )}
          </HStack>
        </HStack>

        {/* ================================================================ */}
        {/* LAYER 1: THE PULSE                                               */}
        {/* ================================================================ */}
        {progress && (
          <Box bg={boxBgColor} p={5} borderRadius="md" border="1px solid" borderColor={borderColor} mb={6}>
            <HStack justify="space-between" mb={3}>
              <HStack spacing={8} align="baseline">
                <VStack align="start" spacing={0}>
                  <Text fontSize="3xl" fontWeight="bold" lineHeight="1">
                    {progress.total_completed}
                  </Text>
                  <Text fontSize="sm" color="gray.500">
                    responses{progress.response_goal ? ` / ${progress.response_goal} goal` : ''}
                  </Text>
                </VStack>
                <VStack align="start" spacing={0}>
                  <Text fontSize="lg" fontWeight="600">
                    {progress.completion_rate}%
                  </Text>
                  <Text fontSize="xs" color="gray.500">completion rate</Text>
                </VStack>
                <VStack align="start" spacing={0}>
                  <Text fontSize="lg" fontWeight="600">
                    {progress.total_sent}
                  </Text>
                  <Text fontSize="xs" color="gray.500">sent</Text>
                </VStack>
                {trancheCost != null && (
                  <VStack align="start" spacing={0}>
                    <Text fontSize="lg" fontWeight="600">
                      ${trancheCost.toFixed(2)}
                    </Text>
                    <Text fontSize="xs" color="gray.500">total cost</Text>
                  </VStack>
                )}
              </HStack>
              {progress.response_goal && (
                <Text fontSize="sm" color={progress.goal_met ? 'green.500' : 'gray.500'} fontWeight="600">
                  {progress.goal_met ? 'Goal reached' : `${progress.progress_percentage}% to goal`}
                </Text>
              )}
            </HStack>
            {progress.response_goal && (
              <Progress
                value={progress.progress_percentage}
                colorScheme={progress.goal_met ? 'green' : 'blue'}
                size="sm"
                borderRadius="md"
              />
            )}
          </Box>
        )}

        {/* ================================================================ */}
        {/* LAYER 2: TOPLINE RESULTS                                         */}
        {/* ================================================================ */}
        {topline && topline.total_responses > 0 ? (
          <VStack spacing={4} align="stretch" mb={6}>
            <Heading as="h2" size="md" color="gray.700">Results</Heading>
            {topline.questions.map((q) => (
              <Box
                key={q.id}
                bg={boxBgColor}
                p={5}
                borderRadius="md"
                border="1px solid"
                borderColor={borderColor}
              >
                <Text fontWeight="600" mb={3} fontSize="sm" color="gray.700">
                  {q.text}
                </Text>

                {q.question_type === 'matrix' ? (
                  /* Matrix: rows with inline choice bars */
                  <VStack spacing={3} align="stretch">
                    {q.rows && q.rows.map((row) => (
                      <Box key={row.id}>
                        <Text fontSize="xs" fontWeight="600" color="gray.600" mb={1}>
                          {row.text}
                        </Text>
                        {row.choices.map((c) => (
                          <ChoiceBar key={c.id} text={c.text} count={c.count} pct={c.pct} />
                        ))}
                      </Box>
                    ))}
                    <Text fontSize="xs" color="gray.400">
                      {q.total_responses} respondent{q.total_responses !== 1 ? 's' : ''}
                    </Text>
                  </VStack>
                ) : (
                  /* single_choice, likert, multi_select */
                  <VStack spacing={1} align="stretch">
                    {q.choices.map((c) => (
                      <ChoiceBar key={c.id} text={c.text} count={c.count} pct={c.pct} />
                    ))}
                    <HStack justify="space-between" mt={1}>
                      <Text fontSize="xs" color="gray.400">
                        {q.total_responses} respondent{q.total_responses !== 1 ? 's' : ''}
                      </Text>
                      {q.multi_select && (
                        <Text fontSize="xs" color="gray.400">
                          Select up to {q.max_selections || 'multiple'}
                        </Text>
                      )}
                    </HStack>
                  </VStack>
                )}
              </Box>
            ))}
          </VStack>
        ) : topline && topline.total_responses === 0 ? (
          <Box bg={boxBgColor} p={8} borderRadius="md" border="1px solid" borderColor={borderColor} mb={6} textAlign="center">
            <Text color="gray.500">No responses yet. Results will appear here as people complete the survey.</Text>
          </Box>
        ) : null}

        {/* ================================================================ */}
        {/* LAYER 3: OPERATIONS (COLLAPSIBLE)                                */}
        {/* ================================================================ */}
        <Box mb={6}>
          <Button
            onClick={() => setShowOperations(!showOperations)}
            variant="ghost"
            size="sm"
            mb={2}
            color="gray.500"
          >
            {showOperations ? 'Hide' : 'Show'} Operations
          </Button>

          <Collapse in={showOperations} animateOpacity>
            <VStack spacing={4} align="stretch">

              {/* SMS Send Queue */}
              <Box bg={boxBgColor} p={5} borderRadius="md" border="1px solid" borderColor={borderColor}>
                <HStack justify="space-between" mb={showSendQueue ? 4 : 0}>
                  <Heading as="h3" size="sm">SMS Send Queue</Heading>
                  <Button onClick={() => setShowSendQueue(!showSendQueue)} size="xs" variant="outline">
                    {showSendQueue ? 'Hide' : 'Show'}
                  </Button>
                </HStack>
                {showSendQueue && (
                  <SendQueue pollId={pollId} poll={poll} onSendComplete={fetchAllData} />
                )}
              </Box>

              {/* Email Send Queue */}
              <Box bg={boxBgColor} p={5} borderRadius="md" border="1px solid" borderColor={borderColor}>
                <HStack justify="space-between" mb={showEmailQueue ? 4 : 0}>
                  <Heading as="h3" size="sm">Email Send Queue</Heading>
                  <Button onClick={() => setShowEmailQueue(!showEmailQueue)} size="xs" variant="outline">
                    {showEmailQueue ? 'Hide' : 'Show'}
                  </Button>
                </HStack>
                {showEmailQueue && (
                  <EmailSendQueue pollId={pollId} onSendComplete={fetchAllData} />
                )}
              </Box>

              {/* Tranche History */}
              <Box bg={boxBgColor} p={5} borderRadius="md" border="1px solid" borderColor={borderColor}>
                <Heading as="h3" size="sm" mb={3}>Tranche History</Heading>
                {tranches.length === 0 ? (
                  <Text color="gray.500" fontSize="sm">No tranches generated yet.</Text>
                ) : (
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th>Tranche</Th>
                        <Th isNumeric>Selected</Th>
                        <Th isNumeric>Reachable</Th>
                        <Th isNumeric>Completed</Th>
                        <Th isNumeric>Rate</Th>
                        {tranches[0]?.cost != null && <Th isNumeric>Cost</Th>}
                      </Tr>
                    </Thead>
                    <Tbody>
                      {tranches.map((t) => (
                        <Tr key={t.tranche}>
                          <Td fontWeight="600">Tranche {t.tranche}</Td>
                          <Td isNumeric>{t.selected || t.sent}</Td>
                          <Td isNumeric>{t.reachable != null ? t.reachable : t.sent}</Td>
                          <Td isNumeric>{t.completed}</Td>
                          <Td isNumeric>{t.completion_rate}%</Td>
                          {t.cost != null && <Td isNumeric>${t.cost.toFixed(2)}</Td>}
                        </Tr>
                      ))}
                      <Tr fontWeight="bold" bg="gray.50">
                        <Td>Total</Td>
                        <Td isNumeric>{tranches.reduce((s, t) => s + (t.selected || t.sent), 0)}</Td>
                        <Td isNumeric>{tranches.reduce((s, t) => s + (t.reachable != null ? t.reachable : t.sent), 0)}</Td>
                        <Td isNumeric>{tranches.reduce((s, t) => s + t.completed, 0)}</Td>
                        <Td isNumeric>
                          {(() => {
                            const totalReachable = tranches.reduce((s, t) => s + (t.reachable != null ? t.reachable : t.sent), 0);
                            const totalCompleted = tranches.reduce((s, t) => s + t.completed, 0);
                            return totalReachable > 0 ? ((totalCompleted / totalReachable) * 100).toFixed(1) : '0';
                          })()}%
                        </Td>
                        {tranches[0]?.cost != null && (
                          <Td isNumeric>${tranches.reduce((s, t) => s + (t.cost || 0), 0).toFixed(2)}</Td>
                        )}
                      </Tr>
                    </Tbody>
                  </Table>
                )}
              </Box>

              {/* A/B Test Results */}
              {abResults && abResults.variants && abResults.variants.length > 0 && (
                <Box bg={boxBgColor} p={5} borderRadius="md" border="1px solid" borderColor={borderColor}>
                  <Heading as="h3" size="sm" mb={3}>A/B Test Results</Heading>
                  <Table variant="simple" size="sm">
                    <Thead>
                      <Tr>
                        <Th>Variant</Th>
                        <Th>Text Preview</Th>
                        <Th isNumeric>Sent</Th>
                        <Th isNumeric>Completed</Th>
                        <Th isNumeric>Rate</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {abResults.variants.map((v) => {
                        const isLeading = abResults.variants.length > 1 &&
                          v.completion_rate === Math.max(...abResults.variants.map(x => x.completion_rate)) &&
                          v.completion_rate > 0;
                        return (
                          <Tr key={v.label} bg={isLeading ? 'green.50' : undefined}>
                            <Td fontWeight="bold">
                              {v.label}
                              {isLeading && <Badge ml={2} colorScheme="green" fontSize="xs">Leading</Badge>}
                            </Td>
                            <Td fontSize="xs" color="gray.600" maxW="300px" isTruncated>{v.text_preview}</Td>
                            <Td isNumeric>{v.sent}</Td>
                            <Td isNumeric>{v.completed}</Td>
                            <Td isNumeric fontWeight={isLeading ? 'bold' : 'normal'}>{v.completion_rate}%</Td>
                          </Tr>
                        );
                      })}
                    </Tbody>
                  </Table>
                </Box>
              )}

              {/* Demographic Balance */}
              {comparisonData.length > 0 && (
                <Box bg={boxBgColor} p={5} borderRadius="md" border="1px solid" borderColor={borderColor}>
                  <Heading as="h3" size="sm" mb={3}>Demographic Balance (Target vs. Current)</Heading>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={comparisonData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="group" angle={-45} textAnchor="end" height={120} />
                      <YAxis label={{ value: 'Percentage', angle: -90, position: 'insideLeft' }} />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="target" fill="#0088FE" name="Target %" />
                      <Bar dataKey="current" fill="#00C49F" name="Current %" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}

              {/* Survey Invitations (Testing) */}
              <Box bg={boxBgColor} p={5} borderRadius="md" border="1px solid" borderColor={borderColor}>
                <HStack justify="space-between" mb={showRespondents ? 3 : 0}>
                  <Heading as="h3" size="sm">Survey Invitations (Testing)</Heading>
                  <Button
                    onClick={() => {
                      if (!showRespondents) fetchRespondents();
                      setShowRespondents(!showRespondents);
                    }}
                    size="xs"
                    variant="outline"
                  >
                    {showRespondents ? 'Hide' : 'Show'}
                  </Button>
                </HStack>
                {showRespondents && (
                  respondents.length === 0 ? (
                    <Text color="gray.500" fontSize="sm">No invitations generated yet.</Text>
                  ) : (
                    <>
                      <Text fontSize="xs" color="gray.500" mb={2}>
                        Click a link to test the survey. Use incognito for multiple responses.
                      </Text>
                      <Table variant="simple" size="sm">
                        <Thead>
                          <Tr>
                            <Th>Tranche</Th>
                            <Th>Phone</Th>
                            <Th>Survey Link</Th>
                            <Th>Done</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {respondents.map((r) => (
                            <Tr key={r.id}>
                              <Td>{r.tranche}</Td>
                              <Td fontFamily="mono" fontSize="xs">{r.phone_number}</Td>
                              <Td>
                                {r.unique_link ? (
                                  <Button
                                    as="a"
                                    href={`/survey/respond/${r.unique_link}`}
                                    target="_blank"
                                    size="xs"
                                    colorScheme="blue"
                                  >
                                    Open
                                  </Button>
                                ) : (
                                  <Text fontSize="xs" color="gray.400">--</Text>
                                )}
                              </Td>
                              <Td>
                                <Badge colorScheme={r.has_completed ? 'green' : 'gray'} fontSize="xs">
                                  {r.has_completed ? 'Yes' : 'No'}
                                </Badge>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </>
                  )
                )}
              </Box>
            </VStack>
          </Collapse>
        </Box>

        {/* Navigation */}
        <HStack spacing={2}>
          <Button as={ReactRouterLink} to={`/manage-questions/${pollId}`} variant="outline" size="sm">
            Back to Poll
          </Button>
          <Button as={ReactRouterLink} to={`/poll-results/${pollId}`} variant="outline" size="sm">
            Full Results
          </Button>
        </HStack>
      </Box>

      {/* Generate Tranche Modal */}
      {poll && audience && (
        <GenerateTrancheModal
          isOpen={isOpen}
          onClose={onClose}
          poll={poll}
          audience={audience}
          onSuccess={handleTrancheGenerated}
          isFirstTranche={tranches.length === 0}
        />
      )}
    </Box>
  );
}

/* ================================================================ */
/* CHOICE BAR — Reusable horizontal bar for showing a single choice */
/* ================================================================ */
function ChoiceBar({ text, count, pct }) {
  const barColor = useColorModeValue('blue.400', 'blue.300');
  const barBg = useColorModeValue('gray.100', 'gray.600');

  return (
    <HStack spacing={3} mb={0.5}>
      <Text fontSize="xs" w="140px" flexShrink={0} textAlign="right" color="gray.600" isTruncated title={text}>
        {text}
      </Text>
      <Box flex="1" position="relative" h="18px" bg={barBg} borderRadius="sm" overflow="hidden">
        <Box
          h="100%"
          w={`${Math.min(pct, 100)}%`}
          bg={barColor}
          borderRadius="sm"
          transition="width 0.3s"
        />
      </Box>
      <Text fontSize="xs" w="65px" flexShrink={0} color="gray.600" fontFamily="mono">
        {pct}% ({count})
      </Text>
    </HStack>
  );
}

export default LivePollingDashboard;
